// @flow
import type {
  ActionHandlers,
  Reducer,
  Selector,
} from '@travel-frontend/state-handling-sdk';
import type {
  PsdLayerType,
  SetSelectedPure,
} from 'redux/uiState/payment/actions';
import type {PaymentState} from 'redux/uiState/payment/PaymentState';
import type {TypeOfPayment} from 'redux/v1/paymentTravel/models';
import type {FetchPaymentTypesSuccess} from 'redux/v1/paymentTravel/paymentTypes/actions';

import {
  createReducerUsingActionHandlers,
  PURE,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {createSelector} from 'reselect';

import {
  SET_CC_CHALLENGE,
  SET_CC_IFRAMEHEIGHT,
  SET_CC_TYPE,
  SET_CC_TYPEERROR,
  SET_FAILED_AUTH_ATTEMPTS,
  SET_INSURANCE_SELECTED,
  SET_PSDLAYER_TYPE,
  SET_SELECTED,
  SET_TO_TOUCHED,
} from 'redux/uiState/payment/actions';
import {paymentState} from 'redux/uiState/UiState';
import {DELETE_PAYMENT as DELETE_INSURANCE_PAYMENT} from 'redux/v1/paymentInsurance/delete/actions';
import {FETCH_PAYMENT as FETCH_INSURANCE_PAYMENT} from 'redux/v1/paymentInsurance/selectedPayment/actions';
import {DELETE_PAYMENT} from 'redux/v1/paymentTravel/delete/actions';
import {PUT_PAYMENT_INVOICE} from 'redux/v1/paymentTravel/invoice/actions';
import {PAYMENT_TYPE_INVOICE} from 'redux/v1/paymentTravel/models';
import {FETCH_PAYMENT} from 'redux/v1/paymentTravel/selectedPayment/actions';

const initState: PaymentState = {};

const actionCreators: ActionHandlers<PaymentState> = {
  [PURE(SET_SELECTED)]: (state: PaymentState, action: SetSelectedPure) => ({
    ...state,
    selected: action.value,
  }),
  [PURE(SET_INSURANCE_SELECTED)]: (
    state: PaymentState,
    action: SetSelectedPure
  ) => ({
    ...state,
    insuranceSelected: action.value,
  }),
  [SUCCESS(FETCH_PAYMENT)]: (
    state: PaymentState,
    action: FetchPaymentTypesSuccess
  ) => ({
    ...state,
    selected: action.data.code,
  }),
  [SUCCESS(FETCH_INSURANCE_PAYMENT)]: (
    state: PaymentState,
    action: FetchPaymentTypesSuccess
  ) => ({
    ...state,
    insuranceSelected: action.data.code,
  }),
  [SUCCESS(DELETE_PAYMENT)]: (
    state: PaymentState,
    action: FetchPaymentTypesSuccess
  ) => ({
    ...state,
    cc: {
      ...state.cc,
      type: undefined,
      typeError: undefined,
      challenge: undefined,
    },
  }),
  [SUCCESS(DELETE_INSURANCE_PAYMENT)]: (
    state: PaymentState,
    action: FetchPaymentTypesSuccess
  ) => ({
    ...state,
    cc: {
      ...state.cc,
      type: undefined,
      typeError: undefined,
      challenge: undefined,
    },
  }),
  [SUCCESS(PUT_PAYMENT_INVOICE)]: (state: PaymentState) => ({
    ...state,
    selected: PAYMENT_TYPE_INVOICE,
  }),
  [PURE(SET_CC_TYPE)]: (state: PaymentState, action: SetSelectedPure) => ({
    ...state,
    cc: {
      ...state.cc,
      type: action.value,
    },
  }),
  [PURE(SET_CC_TYPEERROR)]: (state: PaymentState, action: SetSelectedPure) => ({
    ...state,
    cc: {
      ...state.cc,
      typeError: action.value,
    },
  }),
  [PURE(SET_CC_CHALLENGE)]: (state: PaymentState, action: SetSelectedPure) => ({
    ...state,
    cc: {
      ...state.cc,
      challenge: action.value,
    },
  }),
  [PURE(SET_CC_IFRAMEHEIGHT)]: (
    state: PaymentState,
    action: SetSelectedPure
  ) => ({
    ...state,
    cc: {
      ...state.cc,
      iframeHeight: action.value,
    },
  }),
  [PURE(SET_FAILED_AUTH_ATTEMPTS)]: (state: PaymentState) => ({
    ...state,
    failedAuthAttempts: true,
  }),
  [PURE(SET_PSDLAYER_TYPE)]: (
    state: PaymentState,
    action: SetSelectedPure
  ) => ({
    ...state,
    psdLayer: action.value,
  }),
  [PURE(SET_TO_TOUCHED)]: (state: PaymentState) => ({
    ...state,
    isTouched: true,
  }),
};

export const paymentReducer: Reducer<PaymentState> =
  // $FlowFixMe
  createReducerUsingActionHandlers<PaymentState>(initState, actionCreators);

export const getSelected: Selector<TypeOfPayment> = createSelector(
  paymentState,
  (state: PaymentState) => state.selected
);

export const getInsuranceSelected: Selector<TypeOfPayment | ''> =
  createSelector(
    paymentState,
    (state: PaymentState) => state.insuranceSelected
  );

export const getCcType: Selector<string> = createSelector(
  paymentState,
  (state: PaymentState) => state.cc?.type
);

export const getCcTypeError: Selector<boolean> = createSelector(
  paymentState,
  (state: PaymentState) => state.cc?.typeError
);

export const getCcChallenge: Selector<boolean> = createSelector(
  paymentState,
  (state: PaymentState) => state.cc?.challenge
);

export const getCcIframeHeight: Selector<number> = createSelector(
  paymentState,
  (state: PaymentState) => state.cc?.iframeHeight
);

export const getFailedAuthAttempts: Selector<boolean> = createSelector(
  paymentState,
  (state: PaymentState) => state?.failedAuthAttempts
);

export const getPsdLayerType: Selector<PsdLayerType> = createSelector(
  paymentState,
  (state: PaymentState) => state?.psdLayer
);

export const isPaymentSectionTouched: Selector<boolean> = createSelector(
  paymentState,
  (state: PaymentState) => state?.isTouched
);
